export const profile = {
  name: "Haj Mohamed",
  title: "Senior Backend-Focused Full Stack Developer",
  headline: "Senior Backend-Focused Full Stack Developer",
  location: "Chennai, India",
  tagline: "Crafting robust full-stack web applications that scale.",
  intro:
    "I build and scale backend-first web platforms—APIs, data models, and deployments that stay fast, secure, and maintainable under real production load.",
  highlights: [
    "9+ years delivering production systems across CRM, e-commerce, and enterprise tools",
    "Backend architecture: clean service boundaries, domain-first APIs, scalable foundations",
    "Performance-first: query optimization, payload trimming, latency and throughput tuning",
    "AWS delivery: EC2/S3/RDS deployments with release-ready CI/CD on GitHub Actions",
  ],
  contact: {
    email: "haj8110@gmail.com",
    phone: "+917639666488",
    github: "https://github.com/haj8110",
    linkedin: "https://www.linkedin.com/in/haj-mohamed-593719b5/",
  },
  githubUsername: "haj8110",
  resume: {
    url: "/Haj_Mohamed_Resume.pdf",
    label: "Download Resume",
  },
  availability: {
    headline: "Open to full-time roles and focused freelance engagements.",
    subline:
      "APIs, backend architecture, performance optimization, and AWS deployment.",
  },
};

export const whatIDo = [
  {
    title: "Backend Architecture",
    points: [
      "Design clean service boundaries and domain models that scale with complexity",
      "Build foundations for CRM/CMS/e-commerce style platforms with long-term maintainability",
    ],
  },
  {
    title: "API Development (REST + Real-time)",
    points: [
      "Implement predictable REST contracts: versioning, auth, validation, and error semantics",
      "Build real-time workflows using WebSockets where latency and interactivity matter",
    ],
  },
  {
    title: "Cloud Delivery (AWS)",
    points: [
      "Deploy and operate services on AWS (EC2, S3, RDS) with production discipline",
      "Ship reliably with CI/CD pipelines using GitHub Actions and environment separation",
    ],
  },
  {
    title: "Performance Optimization",
    points: [
      "Identify bottlenecks with profiling; optimize DB queries, caching, and hot paths",
      "Improve system stability and responsiveness under real usage and load",
    ],
  },
];

export const experience = [
  {
    title: "Senior PHP Developer",
    company: "GNB-Tech Software Solutions Pvt Ltd",
    period: "May 2022 – Present",
    highlights: [
      "Own backend delivery for enterprise applications: architecture, APIs, and data modeling",
      "Build and evolve REST APIs for business-critical workflows with stable contracts",
      "Improve production performance by removing bottlenecks in queries, payloads, and compute paths",
      "Support reliability through disciplined releases and operational readiness",
    ],
  },
  {
    title: "Senior Laravel Developer",
    company: "Nexevo Technologies",
    period: "Sep 2019 – Apr 2022",
    highlights: [
      "Led backend architecture decisions for CRM and e-commerce platforms",
      "Delivered maintainable Laravel systems with reusable modules and stable API boundaries",
      "Integrated third-party services (payments/external APIs) with resilient error handling",
      "Reduced delivery risk by standardizing patterns and improving backend consistency",
    ],
  },
  {
    title: "Full Stack Developer",
    company: "PSTS Logistics Pvt Ltd",
    period: "Jul 2018 – Jul 2019",
    highlights: [
      "Built logistics management features across backend APIs and admin dashboards",
      "Delivered operational tools improving workflow visibility, coordination, and reporting",
      "Worked across data + UI layers while maintaining backend-first correctness and reliability",
    ],
  },
];

export const projects = [
  {
    title: "Full Stack Starter Package",
    oneLiner:
      "Production-minded boilerplate to ship backend-first web apps faster.",
    problem:
      "New products repeatedly waste time on setup—routing, API structure, environments, and conventions—creating avoidable early technical debt.",
    solution:
      "A clean Node.js + React starter with scalable project structure, predictable API patterns, and deploy-ready conventions.",
    stack: ["Node.js", "React", "REST APIs"],
    impact:
      "Accelerates kick-off and enforces consistent structure across projects, reducing rework and early-stage tech debt.",
    tags: ["Node.js", "React", "Architecture"],
    links: {
      github: "https://github.com/haj8110",
    },
  },
  {
    title: "Estate Chatbot System",
    oneLiner: "Automates property Q&A and booking flows with consistent responses.",
    problem:
      "Manual handling of inquiries and bookings slows response times and creates inconsistent customer experience.",
    solution:
      "A Node.js chatbot system that automates booking workflows and provides knowledge-based responses for common questions.",
    stack: ["Node.js", "REST APIs", "Automation"],
    impact:
      "Improves response speed and standardizes booking operations, reducing manual coordination effort.",
    tags: ["Node.js", "Chatbot", "Automation"],
    links: {
      github: "https://github.com/haj8110",
    },
  },
  {
    title: "Food Park Backend (AWS Deployment)",
    oneLiner: "AWS-hosted backend with release-ready deployments and scalable APIs.",
    problem:
      "Operational platforms need stable APIs and reliable deployments to avoid downtime and production regressions.",
    solution:
      "A Node.js backend deployed on AWS with production-friendly architecture and CI/CD workflows for repeatable releases.",
    stack: ["Node.js", "AWS (EC2/S3/RDS)", "GitHub Actions"],
    impact:
      "Faster, safer releases and a scalable backend foundation designed for growth and operational stability.",
    tags: ["Node.js", "AWS", "CI/CD"],
    links: {
      github: "https://github.com/haj8110",
    },
  },
  {
    title: "No-as-a-Service",
    oneLiner: "Lightweight Express API that returns a random rejection reason.",
    problem:
      "Bots and apps often need consistent, reusable endpoints for playful or standardized responses.",
    solution:
      "A simple Express API that returns random “no” reasons as a single endpoint, easy to integrate anywhere.",
    stack: ["Node.js", "Express", "API"],
    impact: "A fun, reusable micro-API pattern suitable for demos, bots, and integrations.",
    tags: ["Node.js", "Express", "API"],
    links: {
      github: "https://github.com/hotheadhacker/no-as-a-service",
    },
  },
  {
    title: "Enterprise REST API Platform",
    oneLiner: "Scalable REST APIs with performance and maintainability at the core.",
    problem:
      "As traffic and feature scope grow, APIs degrade without stable contracts, observability-ready design, and performance discipline.",
    solution:
      "Designed REST APIs with clear boundaries, predictable semantics, and performance-focused data access patterns.",
    stack: ["REST APIs", "System Design", "Performance Optimization"],
    impact:
      "Improved platform stability and delivery velocity by keeping APIs evolvable and production-friendly.",
    tags: ["REST", "Performance", "System Design"],
  },
  {
    title: "CRM / E-commerce Integrations",
    oneLiner: "Payments and third-party integrations built for reliability and security.",
    problem:
      "External integrations fail in unpredictable ways and can introduce security and reliability issues if not designed carefully.",
    solution:
      "Integrated third-party services and payment systems with secure patterns, retries, and resilient error handling.",
    stack: ["Laravel", "Integrations", "Payments"],
    impact:
      "Reduced integration risk and improved system reliability under real-world API failures and edge cases.",
    tags: ["Laravel", "Integrations", "Payments"],
  },
];

